import { Injectable, Inject } from '@angular/core';
import { ApiService } from 'src/app/core/services/api.service';
import { UserEvents, UserPermissionEvents } from './events';
import { ENV_CONFIG } from 'src/app/core/injection.token';

type DynamicResponse<T> = app.models.DynamicResponse<T>;

@Injectable()
export class UserAPIService {
  public constructor(
    @Inject(ApiService) private _apiService: ApiService,
    @Inject(ENV_CONFIG) private _envConfig: env.EnvData
  ) {}
  private readonly controller = 'user';

  public getUsers(tenantId: string) {
    const request = 'getUsers';
    const queryParams = { externalTenantId: tenantId };
    const url = this._apiService.buildUrl(
      this._envConfig.TENANT_BACKEND_APISYNC_URL,
      this.controller,
      request,
      queryParams
    );

    return this._apiService.get<app.models.Users[]>(url);
  }

  public getTenantUser(tenantId: string, userId: string) {
    const request = 'getTenantUser';
    const url = this._apiService.buildUrl(this._envConfig.TENANT_BACKEND_APISYNC_URL, this.controller, request);
    let queryParams = { userId: userId } as any;
    if (tenantId) {
      queryParams.externalTenantId = tenantId;
    }

    return this._apiService.get<app.models.UserModel>(url, queryParams);
  }

  public getAdminUsers(pageFilter: app.models.GetAdminUsersPageFilterModel) {
    let queryParams = {
      ...pageFilter,
      startDate: pageFilter.startDate ? pageFilter.startDate.toISOString() : null,
      endDate: pageFilter.endDate ? pageFilter.endDate.toISOString() : null,
    };

    const request = 'getAdminUsers';
    const url = this._apiService.buildUrl(
      this._envConfig.TENANT_BACKEND_APISYNC_URL,
      this.controller,
      request,
      queryParams
    );
    return this._apiService.get<DynamicResponse<app.models.AdminUsersListModel[]>>(url);
  }

  public deleteUser(userId: string, subTenantId: string = undefined) {
    const request = 'deleteUser';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    const data = { userId: userId } as any;
    if (subTenantId) {
      data.subTenantId = subTenantId;
    }

    return this._apiService.postAsync<app.models.ServerResponseModel>(url, data, [UserEvents.UserDeleted]);
  }

  public changeUserState(userId: string, isActive: boolean, subTenantId: string = undefined) {
    const request = 'changeUserState';
    const url = this._apiService.buildUrl(this._envConfig.TENANT_BACKEND_APISYNC_URL, this.controller, request);
    const data = { userId: userId, isActive: isActive } as any;
    if (subTenantId) {
      data.subTenantId = subTenantId;
    }

    return this._apiService.postAsync<app.models.ServerResponseModel>(url, data, [UserEvents.UserStatusChanged]);
  }

  public saveUser(user: app.models.UserModel) {
    const request = 'saveUser';
    const url = this._apiService.buildUrl(this._envConfig.TENANT_BACKEND_APISYNC_URL, this.controller, request);
    const data = {
      userId: user.userId,
      firstName: user.firstName,
      lastName: user.lastName,
      phone: user.phone,
      acceptEmails: user.acceptEmails,
      acceptSms: user.acceptSms,
      legalRepresentative: user.legalRepresentative,
      isActive: user.isActive,
      email: user.email,
      externalTenantId: user.externalTenantId,
      partnerName: user.partnerName,
      partnerUniqueIdentifier: user.partnerUniqueIdentifier,
      userRightReadOnly: user.userRightReadOnly,
      isAdmin: user.isAdmin,
      externalProviderId: user.externalProviderId,
      clientId: this._envConfig.CLIENT_ID,
      serviceCode: this._envConfig.SERVICE_CODE,
      twoFactorAuthenticationTypeId: user.dictionaryTwoFactorAuthenticationTypeId,
    };

    return this._apiService.postAsync<app.models.ServerResponseModel>(url, data, [UserEvents.UserSaved]);
  }

  public getCurrentUserProfileInfo() {
    const request = 'getCurrentUserProfileInfo';
    const url = this._apiService.buildUrl(this._envConfig.TENANT_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.ProfileModel>(url);
  }

  public getUserRights() {
    const request = 'getUserRights';
    const url = this._apiService.buildUrl(this._envConfig.TENANT_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.UserRightsModel>(url);
  }

  public getUserRolesData(userId: string) {
    const request = `getUserRolesData?userId=${userId}`;
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.UserRolesDataModel>(url);
  }

  public getCurrentUserRights(userId: string) {
    const request = `getCurrentUserRights?userId=${userId}`;
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.DictionaryDataModel[]>(url);
  }

  public getFeaturesAndRightsByRole(roleId: string) {
    const request = `getUserFeaturesAndRights?roleId=${roleId}`;
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.UserFeaturesAndRights[]>(url);
  }

  public saveUserPermission(body: app.models.SaveUserRoles) {
    const request = 'saveUserPermission';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.postAsync<app.models.ServerResponseModel>(url, body, [
      UserPermissionEvents.UserPermissionSaved,
    ]);
  }

  public getCurrentUserSubTenants() {
    const request = 'getCurrentUserSubTenants';
    const url = this._apiService.buildUrl(this._envConfig.TENANT_BACKEND_APISYNC_URL, this.controller, request);
    return this._apiService.get<app.models.WorkspaceModel[]>(url);
  }
}
